
<template>
  <div>
    <!-- Elemento video con attributi obbligatori per Safari -->
    <video v-if="isCameraSupported"  ref="video"  autoplay  playsinline  muted class="video-preview"></video>
    
    <br>

    <!-- Pulsante per attivare la camera con gestione browser legacy -->    
    <button v-if="mediaStream && (showCapture || !scanOnly)" @click="captureImage" class="button is-link buttonCSS">
      Take Picture
    </button>
    
    <button v-if="showCameraButton" @click="initCamera" class="button is-link buttonCSS" :disabled="showSpinner">
      <span class="loader" v-if="showSpinner" ></span>
      {{ buttonLabel }}
    </button>      
    
    <div v-if="scanning" class="info-message">
      Scanning... Point the barcode within the camera view
    </div>
    <div v-if="errorMessage && !cameraWorking" class="error-message">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
export default {
  props: {
    scanOnly: {
      type: Boolean,
      default: true
    },
    showCapture: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isCameraSupported: false,
      errorMessage: '',
      mediaStream: null,
      isSafari: false,
      capturedImages: [],
      imgLimit: 3,
      maxReached: false,
      cameraWorking: false,
      showSpinner: false, 
      zxingReader: null,
      scanning: false,
      zxingControls: null,
    }
  },
  
  computed: {
    showCameraButton() {
      return !this.mediaStream && this.isCameraSupported
    },
    
    buttonLabel() {
      return 'Enable Camera';
      // return this.isSafari ? 'Enable Camera (Safari 11+)' : 'Enable Camera'
    }
  },
  
  mounted() {
    this.detectBrowser()
    this.checkCameraSupport()
    this.initCamera()
  },
  
  beforeUnmount() {
    this.stopCamera()
  },
  
  methods: {
    // Avvia una scansione singola con ZXing e si ferma al primo codice trovato
    async startScanning() {
      //if (this.scanning || !this.$refs.video) return
      this.scanning = true

      try {
        const { BrowserMultiFormatReader, BarcodeFormat, DecodeHintType } = await import('@zxing/library')

        const hints = new Map()
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [
          BarcodeFormat.EAN_13,
          BarcodeFormat.CODE_128,
          BarcodeFormat.QR_CODE,
          BarcodeFormat.EAN_8,
          BarcodeFormat.UPC_E,
          BarcodeFormat.UPC_A,
          BarcodeFormat.CODE_39,
          BarcodeFormat.ITF
        ])
        hints.set(DecodeHintType.TRY_HARDER, true)

        this.zxingReader = new BrowserMultiFormatReader(hints)

        // decodeFromVideoElement usa una callback; fermiamo al primo risultato
        this.zxingControls = await this.zxingReader.decodeFromVideoElement(this.$refs.video, (result, err) => {
          if (!this.scanning) return
          if (result && typeof result.getText === 'function') {
            try {
              const rawBytes = (result.getRawBytes && result.getRawBytes()) || null
              const points = (result.getResultPoints && result.getResultPoints()) || []
              const payload = {
                text: result.getText(),
                format: result.getBarcodeFormat && result.getBarcodeFormat(),
                rawBytesLength: rawBytes ? rawBytes.length : 0,
                points: points && points.map(p => ({ x: p.getX ? p.getX() : p.x, y: p.getY ? p.getY() : p.y })),
                timestamp: Date.now(),
              }
              console.log('barcode-scanned payload:', payload)
              this.$emit('barcode-scanned', payload)
            } finally {
              this.stopScanning()
              this.stopCamera()
            }
          }
          // ignora errori intermedi (NotFound, ecc.) per continuare a cercare
        })
        // Fallback timeout: messaggio se non trova nulla dopo 10s
        setTimeout(() => {
          if (this.scanning) {
            this.errorMessage = 'No barcode detected yet. Try better lighting and keep the code steady.'
          }
        }, 10000)
      } catch (e) {
        // Se la camera viene fermata durante la scansione, ignora l'errore
        if (this.scanning) {
          console.error('Barcode scan error:', e)
          this.errorMessage = this.errorMessage || 'Unable to read barcode. Try again with better lighting.'
        }
        this.stopScanning()
      }
    },

    stopScanning() {
      this.scanning = false
      if (this.zxingControls && typeof this.zxingControls.stop === 'function') {
        try { this.zxingControls.stop() } catch (_) {}
      }
      if (this.zxingReader && typeof this.zxingReader.reset === 'function') {
        try { this.zxingReader.reset() } catch (_) {}
      }
      this.zxingReader = null
      this.zxingControls = null
    },

    // Rileva se è Safari e applica polyfill
    detectBrowser() {
      this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
      
      // Polyfill per navigator.mediaDevices
      if (!navigator.mediaDevices) {
        console.log("polyfill")
        navigator.mediaDevices = {}
      }
      
      // Polyfill per getUserMedia su Safari <15
      if (this.isSafari && !navigator.mediaDevices.getUserMedia) {
        console.log("prima di errore")
        navigator.mediaDevices.getUserMedia = function(constraints) {
          return new Promise((resolve, reject) => {
            const legacyGetUserMedia = 
            navigator.webkitGetUserMedia || 
            navigator.mozGetUserMedia
            
            if (legacyGetUserMedia) {
              console.log("legacy get user media")
              legacyGetUserMedia.call(navigator, constraints, resolve, reject)
            } else {
              reject(new Error('getUserMedia non supportato'))
            }
          })
        }
      }
    },
    
    // Verifica il supporto base della camera
    checkCameraSupport() {
      this.isCameraSupported = !!(
      navigator.mediaDevices &&
      navigator.mediaDevices.getUserMedia &&
      typeof MediaStream !== 'undefined'
      )
      
      if (!this.isCameraSupported) {
        this.errorMessage = 'The camera is not supported on this browser'
      }
    },
    
    // Inizializza la camera con configurazione per Safari
    async initCamera() {
      this.showSpinner = true;
      try {
        // Configurazione ottimizzata per Safari
        const constraints = {
          video: {
            facingMode: 'environment',
            width: { min: 640, ideal: 1920 },
            height: { min: 480, ideal: 1080 },
            frameRate: { ideal: 30 }
          }
        }
        
        this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints)
        const video = this.$refs.video
        
        // Workaround per Safari 14
        if (this.isSafari) {
          video.srcObject = this.mediaStream
          await new Promise(resolve => setTimeout(resolve, 500))
        }
        
        video.srcObject = this.mediaStream
        
        // Forza il play programmatico per iOS
        await video.play()
        this.cameraWorking = true;

        // Avvia una singola scansione appena la camera è pronta
        this.startScanning()
        
      } catch (error) {
        this.showSpinner = false;
        this.handleCameraError(error)
      }
      this.showSpinner = false;
    },
    
    // Gestione errori specifica per Safari
    handleCameraError(error) {
      const errorMap = {
        NotAllowedError: 'Permission denied: enable camera in settings',
        NotFoundError: 'No camera available',
        NotReadableError: 'The camera is already in use by another application',
        OverconstrainedError: 'Resolution not supported: need 1920x1080 or lower',
        SecurityError: 'HTTPS request required (uses https://localhost for development)'
      }
      
      this.errorMessage = errorMap[error.name] || `Camera error: ${error.message}`
      
      if (this.isSafari) {
        console.error('Errore Safari:', error)
        // Aggiungi qui eventuali log aggiuntivi per debug
      }
    },
    
    stopCamera() {
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => track.stop())
        this.mediaStream = null
      }
    },
    captureImage() {
      console.log("test save image")
      
      const video = this.$refs.video;
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      
      // Imposta le dimensioni del canvas uguali al video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Disegna l'immagine del video sul canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Converti in URL dati (formato JPEG con qualità 90%)
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9)
      this.$emit('send-picture', { picture: dataUrl });
      this.$emit('picture-captured', { picture: dataUrl });
      //this.capturedImages.push();
      
      
      // this.stopCamera();
      
      //nuovi steps: 
      
      //consegna immagine a genitore
    },
    
  }
}
</script>

<style>
.video-preview {
  width: 100%;
  max-width: 600px;
  height: auto;
  border-radius: 8px;
  /*transform: scaleX(-1);  Mirror effect */
}


.error-message {
  color: #FF3B30;
  padding: 15px;
  border: 1px solid #FF3B30;
  border-radius: 8px;
  margin: 20px 0;
  max-width: 600px;
}


.buttonCSS {
  /* padding: 12px 24px;
  background: #4258FF;
  color: white;
  border: none;
  border-radius: 25px;
  font-size: 16px; */
  margin: 10px 10px 10px 0;
  cursor: pointer;
  transition: transform 0.2s;
  display: inline-block;
  vertical-align: middle;
  width: 100%;
  padding: 15px;
  font-size: 18px;
  /* display: flex;
  justify-content: center; 
  align-items: center; */
  
}

@media (min-width: 480px) {
  /* .buttonCSS:hover {
  transform: scale(1.05);
  } */
  .video-preview {
    /* width: 66% !important; */
    max-width: 100% !important;
  }
  .rightButtons {
    position: absolute;
    right: 0px;
    top:8%;
    width: 32%;
    display: flex;
    flex-wrap: wrap;
    gap: 70%; 
    
    
  }
  /* .buttonCSS{
  
  width:30% !important;
  flex: 0 0 57%; 
  margin-left: 10%;
  } */
  .slotClass button{
    flex: 0 0 57%; 
    margin: 10px 10px 0 0;
    cursor: pointer;
    transition: transform 0.2s;
    display: inline-block;
    vertical-align: middle;
    
    
  }
  .slotClass button:hover{
    transform: scale(1.05);
  }
  .slotClass {
    transform: none !important;
    margin-top:0px;
    
  }
  
}


@media (max-width: 480px) {
  .video-preview {
    max-height: 60vh;
  }
  
  .buttonCSS {
    /* mobile overrides when not scanning only; intentionally minimal */
    line-height: normal;
    /* margin-right: 15px; */
    
    
  }
  .mobileFix{
    padding-left: 0px;
    padding-top: 0px;
    
  }
  
  
}


.loader {
  position: absolute;
  left:3vw;
  z-index: 150;
  width: 7vw;
  height: 7vw;
  border: 5px solid rgba(156, 156, 156, 0.815);;
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  box-sizing: border-box;
  animation: rotation 1s linear infinite;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
} 




</style>